import { createUseStyles } from 'react-jss'
import cx from 'classnames'
import { Navigate } from 'react-router-dom'
import {
  flowMax,
  addProps,
  addState,
  addStateHandlers,
  branch,
  returns,
  addWrapper,
} from 'ad-hok'
import { branchIfPassesPredicate, addEffectOnMount } from 'ad-hok-utils'
import { hasData, isError } from 'shared/src/utils/dataLoading'
import {
  AddLoadingAndErrorIndicators,
  addLoadingContext,
} from 'shared/src/utils/loading'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Loading } from '../components/Loading'

const MIN_INITIAL_LOADING_MS = 1000
const MIN_NORMAL_LOADING_MS = 0

interface StyleProps {
  animate: boolean
}

const useStyles = createUseStyles({
  content: {
    width: '100%',
    height: '100%',
    opacity: ({ animate }: StyleProps) => (animate ? '0%' : '100%'),
    transitionProperty: 'opacity',
    transitionDuration: '0.25s',
  },
  visible: {
    opacity: '100% !important',
  },
})

export const addLoadingAndErrorIndicators: AddLoadingAndErrorIndicators = (
  propName,
  options
) =>
  flowMax(
    addTranslationHelpers,
    branchIfPassesPredicate(propName as any, isError, {
      returns: ({ [propName]: dataLoading, t }) =>
        options?.redirectTo404IfError ? (
          <Navigate to="/" />
        ) : (
          <div>
            {t('error', { message: (dataLoading as any).error.message })}
          </div>
        ),
    }),
    /* eslint-disable ad-hok/dependencies */
    addProps(
      ({ [propName]: dataLoading }) => ({
        shouldAnimate: !hasData(dataLoading),
      }),
      []
    ),
    /* eslint-enable ad-hok/dependencies */
    addState(
      'hasShownLoadingLongEnough',
      'setHasShownLoadingLongEnough',
      ({ [propName]: dataLoading }) => hasData(dataLoading)
    ),
    addLoadingContext,
    addEffectOnMount(
      ({ hasShownInitialLoad, setHasShownLoadingLongEnough }) =>
        () => {
          const interval = setInterval(
            () => {
              setHasShownLoadingLongEnough(true)
            },
            hasShownInitialLoad ? MIN_NORMAL_LOADING_MS : MIN_INITIAL_LOADING_MS
          )

          return () => {
            clearInterval(interval)
          }
        }
    ),
    addStateHandlers(
      ({ [propName]: dataLoading }) => ({
        isLoadingComplete: hasData(dataLoading),
      }),
      {
        setLoadingComplete: () => () => ({
          isLoadingComplete: true,
        }),
      }
    ),
    branch(
      ({ isLoadingComplete }) => !isLoadingComplete,
      returns(
        ({
          [propName]: dataLoading,
          hasShownLoadingLongEnough,
          setLoadingComplete,
        }) => (
          <Loading
            state={
              hasData(dataLoading) && hasShownLoadingLongEnough
                ? 'fadingOut'
                : 'loading'
            }
            onComplete={setLoadingComplete}
          />
        )
      )
    ),
    addProps(({ [propName]: dataLoading }) => ({
      [propName]: (dataLoading as any).data,
    })),
    addState('isVisible', 'setVisible', false),
    addEffectOnMount(({ setVisible }) => () => {
      setVisible(true)
    }),
    addProps(({ shouldAnimate }) => ({
      classes: useStyles({ animate: shouldAnimate }),
    })),
    addWrapper((render, { isVisible, classes }) => (
      <div className={cx(classes.content, isVisible && classes.visible)}>
        {render()}
      </div>
    ))
  ) as any
